import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Fragment, PropsWithChildren } from 'react';
import { GroupSurveyConfigurationModal } from '@components/modals';
import { GroupView } from '@views';
import { DebugPanel } from '@components';
import { NoParamCallback } from 'node:fs';
import { AccountabilityPartnerPairingEditor } from '@components/Admin/AccountabilityPartnerPairingEditor/AccountabilityPartnerPairingEditor';
import { PledgeDialog } from './PledgeDialog';
import { ReportCardDialog } from './ReportCardDialog';
import { EngagementReportDialog } from './EngagementReportDialog';
import { useGroupActionCell, MESSAGE_TYPE } from './UseGroupActionCellController';

interface GroupActionCellProps extends PropsWithChildren {
  group: GroupView;
  updateSummary: NoParamCallback;
}

export const GroupActionCell: React.FC<GroupActionCellProps> = ({ updateSummary, group }) => {
  const {
    anchorEl,
    setAnchorEl,
    moreMenuOpen,
    configureSurveyDialogOpen,
    handleCloseConfigureSurveyDialogModal,
    configurePledgeDialogOpen,
    setConfigurePledgeDialogOpen,
    reportCardDialogOpen,
    setReportCardDialogOpen,
    engagementReportDialogOpen,
    setEngagementReportDialogOpen,
    showGroupMessage,
    groupMessage,
    messageType,
    selectedRecipientIds,
    participants,
    allSelected,
    apSettingOpen,
    setAPSettingOpen,
    sendDisabled,
    isChatMessage,
    handleToggleAll,
    handleToggleRecipient,
    handleSetPledge,
    handleSendMessageMenuItemClick,
    handleReportCard,
    handleEngagementReport,
    handleMessageTypeChange,
    handleGroupMessageChange,
    handleCloseGroupMessage,
    handleSendMessage,
  } = useGroupActionCell(group, updateSummary);

  return (
    <Grid
      container
      sx={{
        flexDirection: 'row',
      }}
    >
      <Fragment>
        <IconButton aria-label="more" role={'button'} onClick={(e) => setAnchorEl(e.currentTarget)} size={'small'}>
          <MoreVert></MoreVert>
        </IconButton>
        <Menu open={moreMenuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem id={'SetPledgeAmount'} key={'pledge'} onClick={handleSetPledge}>
            Set Pledge Amount
          </MenuItem>
          <MenuItem
            id={'EditAccountabilityPartnerPairs'}
            key={'accountabilityPartnerPairs'}
            onClick={() => {
              setAnchorEl(null);
              setAPSettingOpen(true);
            }}
          >
            Edit Accountability Partner Pairings
          </MenuItem>
          {group.channel && (
            <MenuItem id={'SendGroupMessage'} key={'message'} onClick={handleSendMessageMenuItemClick}>
              Send Message to Group
            </MenuItem>
          )}
          <MenuItem id={'ViewReportCard'} key={'reportCard'} onClick={handleReportCard}>
            {group.finalized ? 'View Report Card' : 'View Report Card & Close Group'}
          </MenuItem>
          <MenuItem id={'ViewEngagementReport'} key={'engagementReport'} onClick={handleEngagementReport}>
            View Engagement Report
          </MenuItem>
          <MenuItem
            id={'ViewPrizeDrawings'}
            key={'prizeDrawings'}
            component={Link}
            to={`/admin/groups/${group.id}/prize-drawings`}
            onClick={() => setAnchorEl(null)}
          >
            View Prize Drawings
          </MenuItem>
        </Menu>

        <GroupSurveyConfigurationModal
          key={`${group.id}${group.name}`}
          onClose={handleCloseConfigureSurveyDialogModal}
          group={group}
          open={configureSurveyDialogOpen}
        ></GroupSurveyConfigurationModal>

        <PledgeDialog
          open={configurePledgeDialogOpen}
          groupId={group.id}
          onClose={() => setConfigurePledgeDialogOpen(false)}
          onSaved={() => updateSummary(null)}
        />

        <ReportCardDialog
          open={reportCardDialogOpen}
          onClose={() => setReportCardDialogOpen(false)}
          onSaved={() => updateSummary(null)}
          group={group}
        />

        <EngagementReportDialog
          open={engagementReportDialogOpen}
          onClose={() => setEngagementReportDialogOpen(false)}
          group={group}
        />

        <Dialog
          open={showGroupMessage}
          fullWidth
          maxWidth={false}
          sx={{
            '& .MuiDialog-paper': {
              width: 'calc(100vw - 40px)',
              height: 'calc(100vh - 40px)',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 40px)',
              margin: '20px',
            },
          }}
        >
          <DebugPanel displayItem={group}></DebugPanel>
          <DialogTitle>Send Group Message</DialogTitle>
          <DialogContent style={{ paddingTop: 8 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="message-type-label">What type of message do you want to send?</InputLabel>
              <Select
                labelId="message-type-label"
                value={messageType}
                label="What type of message do you want to send?"
                onChange={handleMessageTypeChange}
              >
                <MenuItem value={MESSAGE_TYPE.CHAT}>Chat</MenuItem>
                <MenuItem value={MESSAGE_TYPE.PROGRAM_REMINDER}>Program Reminder</MenuItem>
                <MenuItem value={MESSAGE_TYPE.SMS}>SMS</MenuItem>
              </Select>
            </FormControl>

            <TextField
              minRows={5}
              multiline
              fullWidth
              label="Message"
              value={groupMessage}
              onChange={(e) => handleGroupMessageChange(e.currentTarget.value)}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                border: 1,
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                px: 2,
                py: 1,
                opacity: isChatMessage ? 0.5 : 1,
              }}
            >
              {isChatMessage && (
                <Box sx={{ py: 1, color: 'text.secondary', fontStyle: 'italic', fontSize: 14 }}>
                  Chat messages are sent to everyone in the group.
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedRecipientIds.length > 0 && !allSelected}
                    onChange={handleToggleAll}
                    disabled={isChatMessage}
                  />
                }
                label="Select All"
                sx={{ borderBottom: 1, borderColor: 'rgba(0, 0, 0, 0.23)', width: '100%', mb: 1 }}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                {participants.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={selectedRecipientIds.includes(p.id)}
                        onChange={() => handleToggleRecipient(p.id)}
                        disabled={isChatMessage}
                      />
                    }
                    label={p.name || p.email}
                  />
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseGroupMessage}>Close</Button>
            <Button variant={'contained'} onClick={handleSendMessage} disabled={sendDisabled}>
              Send
            </Button>
          </DialogActions>
        </Dialog>

        <AccountabilityPartnerPairingEditor
          open={apSettingOpen}
          closeDialog={() => setAPSettingOpen(false)}
          groupId={group.id}
        ></AccountabilityPartnerPairingEditor>
      </Fragment>
    </Grid>
  );
};
